import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Form, Button, Card, Spinner, Alert, Table, Modal } from 'react-bootstrap';
import SelectControl from './common/SelectControl';
import DarkHeader from './common/DarkHeader';

function ApprovalsPage({ onBack, token, onLogout, onSettings }) {
    const [bookings, setBookings] = useState([]);
    const [allBookings, setAllBookings] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [filters, setFilters] = useState({});
    const [selectedGroup, setSelectedGroup] = useState('0');
    const [selectedExpert, setSelectedExpert] = useState('0');
    const [selectedStatus, setSelectedStatus] = useState('Open');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedBookings, setSelectedBookings] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [modalBookingIds, setModalBookingIds] = useState([]);
    const [modalAction, setModalAction] = useState(null); // 'approve' or 'decline'
    const [starRating, setStarRating] = useState(0);
    const [ratingComment, setRatingComment] = useState('');
    const [starError, setStarError] = useState(false);
    const [showDescriptionModal, setShowDescriptionModal] = useState(false);
    const [selectedDescription, setSelectedDescription] = useState('');

    const fetchFilters = async () => {
        try {
            const response = await fetch('/api/approvals/filters.php', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch filters');
            }

            const data = await response.json();
            setFilters(data);
            return data; // Return the data for awaiting
        } catch (err) {
            setError('Failed to load filters: ' + err.message);
            throw err; // Re-throw for proper error handling
        }
    };

    const fetchBookings = useCallback(async (append = false) => {
        setLoading(true);
        setError(null);

        try {
            const pageInput = document.getElementById('pagination-page');
            const currentPage = pageInput ? parseInt(pageInput.value) || 1 : 1;

            const params = new URLSearchParams({
                group: selectedGroup,
                expert: selectedExpert,
                status: selectedStatus,
                date_from: dateFrom,
                date_to: dateTo,
                page: currentPage
            });

            const response = await fetch(`/api/approvals/index.php?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch bookings');
            }

            const data = await response.json();
            setPagination(data.pagination);
            
            if (append) {
                // Append new bookings to existing ones (functional update prevents stale state)
                setAllBookings(prev => [...prev, ...(data.bookings || [])]);
            } else {
                // Replace bookings (new search)
                setAllBookings(data.bookings || []);
            }
        } catch (err) {
            setError('Failed to load bookings: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [selectedGroup, selectedExpert, selectedStatus, dateFrom, dateTo, token]);

    useEffect(() => {
        const initializePage = async () => {
            await fetchFilters();
            // Ensure defaults for initial Open fetch (no date limits, all groups/experts)
            setSelectedStatus('Open');
            setSelectedGroup('0');
            setSelectedExpert('0');
            setDateFrom('');
            setDateTo('');
            // Fetch bookings after filters are loaded with defaults applied
            await fetchBookings();
        };
        initializePage();
    }, []);

    // Clear results when filter values change
    useEffect(() => {
        const pageInput = document.getElementById('pagination-page');
        if (pageInput && pageInput.value !== '1') {
            setAllBookings([]);
            pageInput.value = '1';
        }
    }, [selectedGroup, selectedExpert, selectedStatus, dateFrom, dateTo]);

    // Handle search button click
    const handleSearch = async () => {
        const pageInput = document.getElementById('pagination-page');
        if (pageInput) pageInput.value = '1';
        
        setAllBookings([]); // Clear existing results
        setSelectedBookings([]); // Clear selections
        setSelectAll(false);
        await fetchBookings();
    };

    const handleLoadMore = async () => {
        const pageInput = document.getElementById('pagination-page');
        if (pageInput) {
            const nextPage = (parseInt(pageInput.value) || 1) + 1;
            pageInput.value = nextPage.toString();
            await fetchBookings(true); // true = append mode
        }
    };

    // Handle individual booking selection
    const handleSelectBooking = (bookingId, isSelected) => {
        if (isSelected) {
            setSelectedBookings([...selectedBookings, bookingId]);
        } else {
            setSelectedBookings(selectedBookings.filter(id => id !== bookingId));
        }
    };

    // Handle select all checkbox
    const handleSelectAll = (isSelected) => {
        setSelectAll(isSelected);
        if (isSelected) {
            // Select all actionable bookings
            const actionableIds = allBookings
                .filter(booking => booking.status === "Open" || booking.status === "Disputed")
                .map(booking => booking.id);
            setSelectedBookings(actionableIds);
        } else {
            setSelectedBookings([]);
        }
    };

    // Handle individual approve/decline
    const handleIndividualAction = (bookingId, action) => {
        setModalBookingIds([bookingId]);
        setModalAction(action);
        setShowApprovalModal(true);
    };

    // Handle bulk approve/decline
    const handleBulkAction = (action) => {
        if (selectedBookings.length === 0) {
            alert('Please select at least one booking');
            return;
        }
        setModalBookingIds(selectedBookings);
        setModalAction(action);
        setShowApprovalModal(true);
    };

    // Handle modal submission
    const handleModalSubmit = async () => {
        // Validate star rating only for approve action
        if (modalAction === 'approve') {
            if (starRating === 0) {
                setStarError(true);
                return;
            }
        }
        
        // For decline, set a default low rating (1 star) if not provided
        const finalRating = modalAction === 'decline' && starRating === 0 ? 1 : starRating;
        
        // Show decline-specific confirmation first
        if (modalAction === 'decline') {
            const confirmed = window.confirm(
                "Are you sure you want to decline this booking and begin the disputed booking procedure?"
            );
            if (!confirmed) {
                return;
            }
        }
        
        // Then warn if rating < 4 (only for approve with low stars)
        if (modalAction === 'approve' && finalRating < 4) {
            const confirmed = window.confirm(
                "Please note! Scoring delivery bookings with 3 or less stars will open a dispute in relation to this work, triggering an investigation. " +
                "Are you sure you would like to open a dispute relating to this booking?"
            );
            if (!confirmed) {
                return;
            }
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const formData = new FormData();
            modalBookingIds.forEach(id => { formData.append('booking_ids[]', id); });
            formData.append('star_rating', finalRating);
            formData.append('comment', ratingComment);
            
            const response = await fetch('/api/approvals/approve.php', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                
                throw new Error('Failed to process bookings: ' + (errorData.error || 'Unknown error'));
            }
            
            const data = await response.json();
            
            // Close modal and reset
            setShowApprovalModal(false);
            setStarRating(0);
            setRatingComment('');
            setModalBookingIds([]);
            setSelectedBookings([]);
            setSelectAll(false);
            
            // Refresh bookings
            const pageInput = document.getElementById('pagination-page');
            if (pageInput) pageInput.value = '1';
            setAllBookings([]);
            await fetchBookings();
            
            alert(`Successfully ${data.is_approved ? 'approved' : 'disputed'} ${data.updated.length} booking(s)`);
        } catch (err) {
            setError('Failed to process bookings: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatAmount = (amount, isExpenses) => {
        if (!amount) return '-';
        const prefix = isExpenses ? '£' : '';
        return `${prefix}${amount}`;
    };

    const handleViewDescription = (description) => {
        setSelectedDescription(description || 'No description available.');
        setShowDescriptionModal(true);
    };

    const getStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case 'open': return '#4caf50'; // Green
            case 'satisfied': return '#ff9800'; // Orange
            case 'deleted': return '#dc143c'; // Crimson
            case 'disputed': return '#9c27b0'; // Purple
            default: return '#343434'; // Dark
        }
    };

    return (
        <div className="page-container">
            <div className="bg-blur-indigo"></div>
            <div className="bg-blur-fuchsia"></div>
            
            <DarkHeader
                title="Approvals"
                subtitle="Review and approve pending bookings"
                showBackButton={false}
            />

            <Container fluid style={{ position: 'relative', zIndex: 10 }}>
                {/* Filters Section */}
                <Card className="mb-4">
                    <Card.Body>
                        <Card.Title>Filter Bookings</Card.Title>
                        <Row>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Select Group</Form.Label>
                                    <SelectControl
                                        value={selectedGroup}
                                        onChange={setSelectedGroup}
                                        options={[
                                            { value: '0', label: 'All Groups' },
                                            ...(filters.groups || []).map(g => ({ value: String(g.id), label: g.name }))
                                        ]}
                                        placeholder="Select group..."
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Select Expert</Form.Label>
                                    <SelectControl
                                        value={selectedExpert}
                                        onChange={setSelectedExpert}
                                        options={[
                                            { value: '0', label: 'All Experts' },
                                            ...(filters.experts || []).map(e => ({ value: String(e.id), label: e.name }))
                                        ]}
                                        placeholder="Select expert..."
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Select Status</Form.Label>
                                    <SelectControl
                                        value={selectedStatus}
                                        onChange={setSelectedStatus}
                                        options={(filters.statuses || []).map(s => ({ value: s.name, label: s.name }))}
                                        placeholder="Select status..."
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date Range</Form.Label>
                                    <Row>
                                        <Col>
                                            <Form.Control
                                                type="date"
                                                placeholder="From Date"
                                                value={dateFrom}
                                                onChange={(e) => setDateFrom(e.target.value)}
                                                className="date-picker"
                                            />
                                        </Col>
                                        <Col>
                                            <Form.Control
                                                type="date"
                                                placeholder="To Date"
                                                value={dateTo}
                                                onChange={(e) => setDateTo(e.target.value)}
                                                className="date-picker"
                                            />
                                        </Col>
                                    </Row>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Button variant="primary" onClick={handleSearch} disabled={loading}>
                                    {loading ? <Spinner size="sm" className="me-2" /> : null}
                                    Search
                                </Button>
                            </Col>
                        </Row>
                        
                        {/* Hidden input to store current page number */}
                        <input type="hidden" id="pagination-page" defaultValue="1" />
                    </Card.Body>
                </Card>

                {/* Bulk Actions */}
                {selectedBookings.length > 0 && (
                    <Card className="mb-4">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col>
                                    <strong>{selectedBookings.length} booking(s) selected</strong>
                                </Col>
                                <Col md="auto">
                                    <Button 
                                        variant="success" 
                                        className="me-2"
                                        onClick={() => handleBulkAction('approve')}
                                    >
                                        Approve Selected
                                    </Button>
                                    <Button 
                                        variant="danger"
                                        onClick={() => handleBulkAction('decline')}
                                    >
                                        Decline Selected
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                )}

                {/* Error Display */}
                {error && (
                    <Alert variant="danger" className="mb-4">
                        {error}
                    </Alert>
                )}

                {/* Bookings Table */}
                <Card>
                    <Card.Body>
                        {/* Desktop Table */}
                        <div className="table-responsive">
                            <Table striped hover>
                                <thead>
                                    <tr>
                                        <th>
                                            <Form.Check
                                                type="checkbox"
                                                checked={selectAll}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                            />
                                        </th>
                                        <th>Group</th>
                                        <th>Expert</th>
                                        <th>Type</th>
                                        <th>Title</th>
                                        <th>Units / GBP</th>
                                        <th>Delivery Date</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allBookings.map((booking) => (
                                        <tr key={booking.id} id={`row-${booking.id}`}>
                                            <td>
                                                {(booking.status === "Open" || booking.status === "Disputed") ? (
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={selectedBookings.includes(booking.id)}
                                                        onChange={(e) => handleSelectBooking(booking.id, e.target.checked)}
                                                    />
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td>{booking.group_name}</td>
                                            <td>{booking.expert_name}</td>
                                            <td>{booking.booking_type}</td>
                                            <td>
                                                <span className={booking.is_expenses ? 'text-expense' : ''} style={{ fontWeight: booking.is_expenses ? 'bold' : 'normal' }}>
                                                    {booking.booking_title}
                                                </span>
                                                {booking.booking_long_description && booking.booking_long_description.trim() !== '' && (
                                                    <span style={{ marginLeft: '0.5rem' }}>
                                                        <a 
                                                            href="#" 
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleViewDescription(booking.booking_long_description);
                                                            }}
                                                            style={{ 
                                                                fontSize: '0.875rem', 
                                                                color: '#60a5fa',
                                                                textDecoration: 'none'
                                                            }}
                                                        >
                                                            View Description
                                                        </a>
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={booking.is_expenses ? 'text-expense' : ''} style={{ fontWeight: booking.is_expenses ? 'bold' : 'normal' }}>
                                                    {formatAmount(booking.amount, booking.is_expenses)}
                                                </span>
                                            </td>
                                            <td>{booking.delivery_date}</td>
                                            <td>
                                                <span className={`status-chip status-${booking.status.toLowerCase()}`}>
                                                    {booking.status}
                                                </span>
                                            </td>
                                            <td>
                                                {(booking.status === "Open" || booking.status === "Disputed") ? (
                                                    <div className="d-flex gap-2">
                                                        <Button 
                                                            variant="success" 
                                                            size="sm"
                                                            onClick={() => handleIndividualAction(booking.id, 'approve')}
                                                        >
                                                            Approve
                                                        </Button>
                                                        <Button 
                                                            variant="danger" 
                                                            size="sm"
                                                            onClick={() => handleIndividualAction(booking.id, 'decline')}
                                                        >
                                                            Decline
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        {/* Mobile Cards */}
                        <div className="mobile-cards">
                            {allBookings.some(b => b.status === "Open" || b.status === "Disputed") && (
                                <div className="booking-card" style={{marginBottom: '20px'}}>
                                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                        <div>
                                            <h6 style={{margin: 0, fontWeight: '600'}}>Select All Bookings</h6>
                                            <small className="text-muted">
                                                {allBookings.filter(b => b.status === "Open" || b.status === "Disputed").length} actionable bookings
                                            </small>
                                        </div>
                                        <Form.Check 
                                            type="checkbox" 
                                            checked={selectAll}
                                            onChange={(e) => handleSelectAll(e.target.checked)}
                                            style={{transform: 'scale(1.2)'}}
                                        />
                                    </div>
                                </div>
                            )}
                            
                            {allBookings.map(booking => (
                                <div key={booking.id} id={`row-${booking.id}`} className="booking-card">
                                    {/* Header */}
                                    <div className="booking-card-header">
                                        {/* Checkbox */}
                                        {(booking.status === "Open" || booking.status === "Disputed") && (
                                            <div className="booking-card-checkbox">
                                                <Form.Check 
                                                    type="checkbox"
                                                    checked={selectedBookings.includes(booking.id)}
                                                    onChange={(e) => handleSelectBooking(booking.id, e.target.checked)}
                                                />
                                            </div>
                                        )}
                                        
                                        <div className="booking-card-title-section">
                                            <h6 className="booking-card-title">{booking.expert_name}</h6>
                                            <p className="booking-card-subtitle">{booking.group_name}</p>
                                        </div>
                                        
                                        <span className={`booking-card-status ${booking.status.toLowerCase()}`}>
                                            {booking.status}
                                        </span>
                                    </div>
                                    
                                    {/* Body */}
                                    <div className="booking-card-body">
                                        <div className="booking-card-field">
                                            <span className="booking-card-label">Booking Type</span>
                                            <div className="booking-card-value">
                                                {booking.is_expenses && (
                                                    <div className="text-expense" style={{fontWeight: 'bold', marginBottom: '4px'}}>Expenses</div>
                                                )}
                                                {booking.booking_type}
                                                <br />
                                                <small className="text-muted">{booking.booking_title}</small>
                                                {booking.booking_long_description && booking.booking_long_description.trim() !== '' && (
                                                    <div style={{ marginTop: '0.25rem' }}>
                                                        <a 
                                                            href="#" 
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleViewDescription(booking.booking_long_description);
                                                            }}
                                                            style={{ 
                                                                fontSize: '0.875rem', 
                                                                color: '#60a5fa',
                                                                textDecoration: 'none'
                                                            }}
                                                        >
                                                            View Description
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="booking-card-field">
                                            <span className="booking-card-label">Delivery Date</span>
                                            <span className="booking-card-value">{booking.delivery_date}</span>
                                        </div>
                                        
                                        <div className="booking-card-field">
                                            <span className="booking-card-label">Units / GBP</span>
                                            <span className="booking-card-value">{formatAmount(booking.amount, booking.is_expenses)}</span>
                                        </div>
                                        
                                        <div className="booking-card-field">
                                            <span className="booking-card-label">Approved By</span>
                                            <span className="booking-card-value">{booking.approved_by || '-'}</span>
                                        </div>
                                    </div>
                                    
                                    {/* Actions */}
                                    {(booking.status === "Open" || booking.status === "Disputed") && (
                                        <div className="booking-card-actions">
                                            <Button 
                                                variant="success" 
                                                onClick={() => handleIndividualAction(booking.id, 'approve')}
                                            >
                                                Approve
                                            </Button>
                                            <Button 
                                                variant="danger"
                                                onClick={() => handleIndividualAction(booking.id, 'decline')}
                                            >
                                                Decline
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Load More Button */}
                        {pagination && pagination.has_more && (
                            <div className="text-center mt-4">
                                <Button 
                                    variant="outline-primary" 
                                    onClick={handleLoadMore}
                                    disabled={loading}
                                >
                                    {loading ? <Spinner size="sm" className="me-2" /> : null}
                                    Load More
                                </Button>
                            </div>
                        )}

                        {/* Results Summary */}
                        {pagination && (
                            <div className="text-muted mt-3">
                                Showing {allBookings.length} of {pagination.total_bookings} bookings
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Container>

            {/* Approval Modal */}
            <Modal show={showApprovalModal} onHide={() => setShowApprovalModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalAction === 'approve' ? 'Approve' : 'Decline'} Booking{modalBookingIds.length > 1 ? 's' : ''}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalAction === 'approve' && (
                        <div className="mb-3">
                            <label className="form-label">Rating (Required)</label>
                            <div className="d-flex gap-1">
                                {[1, 2, 3, 4, 5].map(star => (
                                    <button
                                        key={star}
                                        type="button"
                                        className={`btn ${star <= starRating ? 'btn-warning' : 'btn-outline-warning'}`}
                                        onClick={() => setStarRating(star)}
                                        style={{ fontSize: '20px' }}
                                    >
                                        ★
                                    </button>
                                ))}
                            </div>
                            {starError && <div className="text-danger small">Please select a rating</div>}
                        </div>
                    )}
                    <div className="mb-3">
                        <label className="form-label">Comment (Optional)</label>
                        <textarea
                            className="form-control"
                            rows="3"
                            value={ratingComment}
                            onChange={(e) => setRatingComment(e.target.value)}
                            placeholder="Add a comment..."
                        ></textarea>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowApprovalModal(false)}>
                        Cancel
                    </Button>
                    <Button 
                        variant={modalAction === 'approve' ? 'success' : 'danger'}
                        onClick={handleModalSubmit}
                    >
                        {modalAction === 'approve' ? 'Approve' : 'Decline'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Description Modal */}
            <Modal show={showDescriptionModal} onHide={() => setShowDescriptionModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Booking Description</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ 
                        maxHeight: '400px', 
                        overflowY: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word'
                    }}>
                        {selectedDescription}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDescriptionModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default ApprovalsPage;
